/**
 * schemaToExample — walk a JSON Schema (as produced by zod-to-json-schema)
 * and synthesize a plausible example payload. Used by the Trigger modal's
 * JSON input to pre-fill the editor so users see *what shape* the
 * endpoint expects rather than a blank "{}".
 *
 * Preference order for each field:
 *   1. schema.default          ← author already declared the canonical example
 *   2. schema.examples[0]      ← Zod's .describe() can hang examples here
 *   3. schema.const            ← single literal
 *   4. type-based stub         ← '' / 0 / false / [] / {}
 *
 * Required-only mode (`mode: 'required'`) emits only required fields, so
 * the example is a minimal payload the user can submit as-is. Default
 * (`mode: 'full'`) emits every declared field, so users see the full
 * surface and can delete what they don't need.
 *
 * Refuses to recurse infinitely on $ref-cycles; bails out at depth 4.
 */

const MAX_DEPTH = 4;

function exampleFor(schema, depth = 0, mode = 'full') {
  if (!schema || typeof schema !== 'object' || depth > MAX_DEPTH) return null;

  // Authored hints win first.
  if (schema.default !== undefined) return schema.default;
  if (Array.isArray(schema.examples) && schema.examples.length > 0) return schema.examples[0];
  if (schema.const !== undefined) return schema.const;
  if (Array.isArray(schema.enum) && schema.enum.length > 0) return schema.enum[0];

  const t = schema.type;

  if (t === 'string') {
    if (schema.format === 'uri' || schema.format === 'url') return 'https://example.com';
    if (schema.format === 'email') return 'user@example.com';
    if (schema.format === 'date-time') return new Date().toISOString();
    return '';
  }
  if (t === 'integer') return 0;
  if (t === 'number') return 0;
  if (t === 'boolean') return false;

  if (t === 'array') {
    // Emit one example element so the shape is visible, not just `[]`.
    if (schema.items) {
      const item = exampleFor(schema.items, depth + 1, mode);
      return item === null ? [] : [item];
    }
    return [];
  }

  if (t === 'object' && schema.properties) {
    const out = {};
    const required = new Set(schema.required || []);
    for (const [k, v] of Object.entries(schema.properties)) {
      if (mode === 'required' && !required.has(k)) continue;
      const val = exampleFor(v, depth + 1, mode);
      if (val !== null) out[k] = val;
    }
    return out;
  }

  // anyOf / oneOf — pick the first branch.
  for (const key of ['anyOf', 'oneOf', 'allOf']) {
    if (Array.isArray(schema[key]) && schema[key].length > 0) {
      return exampleFor(schema[key][0], depth + 1, mode);
    }
  }

  return null;
}

/**
 * Public entrypoint. Returns a JS object (or `{}` for null/empty schemas).
 *
 * @param {object|null} schema  JSON Schema (zod-to-json-schema output)
 * @param {object}      opts
 * @param {string}      [opts.mode='full']  'full' | 'required'
 */
export function schemaToExample(schema, { mode = 'full' } = {}) {
  if (!schema) return {};
  const result = exampleFor(schema, 0, mode);
  return result && typeof result === 'object' && !Array.isArray(result) ? result : {};
}

/**
 * Pretty-print a value as 2-space-indented JSON, never throwing on
 * circular refs (uses a safe replacer).
 */
export function prettyJson(value) {
  const seen = new WeakSet();
  return JSON.stringify(value, (_k, v) => {
    if (typeof v === 'object' && v !== null) {
      if (seen.has(v)) return '[Circular]';
      seen.add(v);
    }
    return v;
  }, 2);
}
